
import type * as React from 'react';
import { cn } from '@/lib/utils';

export type BadgeTone =
  | 'default'
  | 'warning'
  | 'highlight'
  | 'info'
  | 'soft'
  | 'outline'
  | 'secondary'
  | 'success'
  | 'destructive'
  | 'sale';

export interface BadgeProps extends Omit<React.ComponentPropsWithoutRef<'span'>, 'children'> {
  tone?: BadgeTone;
  /** `strong`: the offer mark (bold, uppercase, tracked, padded, flat: no shadow) — the one
   *  recipe for urgency and discount pills, so they never drift apart. */
  emphasis?: 'normal' | 'strong';
  /** The pill sits on a photograph: a 1px white hairline keeps its edge on any picture (a
   *  red pill on a red shirt), and disappears on a light one. Never a shadow: flat. */
  overlay?: boolean;
  /** `sm`: compact tag for dense list rows (status and recurrence under an expense). */
  size?: 'md' | 'sm';
  children: React.ReactNode;
}

const BADGE_TONE: Record<BadgeTone, string> = {
  default: 'bg-medlux text-medlux-foreground',
  // Needs attention (pendente, aguardando): the ds mel tint with dark honey text.
  warning: 'bg-warning-subtle text-warning-strong',
  // Something new or set apart (Novo, 1ª consulta, contagens): the ds coral accent.
  highlight: 'bg-highlight-soft text-highlight-foreground',
  // Informational, and a session that is booked but not yet confirmed (ds "Agendada").
  info: 'bg-info-subtle text-info',
  soft: 'bg-medlux-soft text-medlux-soft-foreground',
  outline: 'border border-border text-foreground',
  secondary: 'bg-secondary text-secondary-foreground',
  success: 'bg-success-soft text-success-soft-foreground',
  destructive: 'bg-destructive-soft text-destructive-soft-foreground',
  // Offers and deadlines only ("N% OFF", "Últimos dias"): the one red on the page.
  sale: 'bg-sale text-sale-foreground',
};

export function Badge({ tone = 'soft', emphasis = 'normal', overlay = false, size = 'md', className, children, ...props }: BadgeProps) {
  return (
    <span
      {...props}
      className={cn(
        'inline-flex items-center rounded-full whitespace-nowrap',
        emphasis === 'strong'
          ? 'gap-1 px-3 py-1 text-[0.6875rem] font-bold uppercase tracking-[0.06em]'
          : size === 'sm'
            ? 'min-h-6 gap-1 px-2 py-0 text-[0.6875rem] font-semibold'
            : 'min-h-7 gap-1.5 px-2.5 py-0.5 text-xs font-semibold',
        BADGE_TONE[tone],
        overlay && 'ring-1 ring-white/70',
        className,
      )}
    >
      {children}
    </span>
  );
}
